/**
 * Command Bus（DESIGN.md §4.5、CLAUDE.md 规则 5）。
 *
 * **所有文档修改都必须经过这里。** 文档类型全字段 readonly，唯一能产出新文档的
 * 是 DocumentDraft，而 draft 只在命令执行时被创建——这条链把"规则 5"从纪律
 * 变成了结构性事实。
 *
 * 每个命令必须能 {@link Command.invert} 出逆操作，这是撤销系统的基础：
 *
 * - **结构性命令**（增删图层、改属性、编组）的逆操作只存参数，成本近乎为零。
 *   §4.5 明确要求"结构性命令的 invert 不复制像素"。
 * - **像素命令**（画笔、滤镜）的逆操作走 TileStore 的 COW 快照（M1-02），
 *   同样不复制像素——快照只是 refCount++。
 *
 * 执行失败时文档整体回滚，不留半成品：命令是原子的，要么全成要么全不动。
 */
import type { Document } from '../document/types';
import { DocumentDraft } from '../document/draft';

/** JSON 可序列化的值——`record.args` 的约束（ADR-0007）。 */
export type Json = string | number | boolean | null | readonly Json[] | { readonly [key: string]: Json };

/**
 * 命令。
 *
 * `apply` 在草稿上做修改，`invert` 产出能撤销它的另一条命令。
 * 二者必须严格互逆——M1-09 会用随机命令序列反复验证这一点。
 */
export interface Command {
  /** 命令种类，用于日志与历史面板显示。 */
  readonly kind: string;
  /** 面向用户的描述，出现在历史面板里（"删除图层"而非 "removeLayer"）。 */
  readonly label: string;
  /**
   * 历史成本估算（字节）。用于历史预算（§4.5：512MB / 100 步）。
   * 结构性命令应当远小于 1KB。
   */
  readonly cost: number;
  /**
   * 动作录制用（M4-04 / ADR-0007）：构造这条命令的纯数据参数，缺省表示
   * 这条命令携带了不可 JSON 序列化的内容（如 `TileHandle`/`Layer` 子树），
   * 录制器据此跳过它而不是假装录下来。
   */
  readonly record?: { readonly kind: string; readonly args: Readonly<Record<string, Json>> };
  apply(draft: DocumentDraft): void;
  /**
   * 产出逆命令。
   *
   * **必须在 apply 之前调用**——逆操作往往需要读取"改之前"的状态
   * （比如删除图层的逆操作要知道被删的是什么、在哪个位置）。
   */
  invert(document: Document): Command | null;
}

export interface CommandResult {
  readonly document: Document;
  readonly command: Command;
  /** 撤销本次修改的命令。为 null 表示该命令不可撤销。 */
  readonly inverse: Command | null;
  /** 文档是否真的变了。空操作不该进历史。 */
  readonly changed: boolean;
}

export type CommandListener = (result: CommandResult) => void;

/** 命令执行失败时抛出，携带失败前的文档以便调用方确认未被污染。 */
export class CommandFailedError extends Error {
  constructor(
    readonly command: Command,
    // Error 基类已有 cause，这里显式 override 而不是换个名字：
    // 保持与标准 Error 一致，调用方的 error.cause 才能拿到真正的原因
    override readonly cause: unknown,
  ) {
    super(`命令「${command.label}」执行失败：${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = 'CommandFailedError';
  }
}

export interface CommandBusOptions {
  readonly document: Document;
  /** 提供当前时间。注入而非直接调 Date.now()，测试才能得到确定的结果。 */
  readonly now: () => string;
}

export class CommandBus {
  #document: Document;
  readonly #now: () => string;
  readonly #listeners = new Set<CommandListener>();
  #dispatching = false;

  constructor(options: CommandBusOptions) {
    this.#document = options.document;
    this.#now = options.now;
  }

  get document(): Document {
    return this.#document;
  }

  subscribe(listener: CommandListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /**
   * 执行一条命令。
   *
   * 失败时**文档保持原样**：草稿是独立对象，抛异常时它连同其中间状态一起被丢弃，
   * 当前文档引用从未被替换过。这就是"不留半成品"的实现方式——
   * 不需要显式回滚，因为根本没有就地修改。
   */
  dispatch(command: Command): CommandResult {
    if (this.#dispatching) {
      // 命令里再 dispatch 命令会让 invert 的配对关系错乱：
      // 内层命令的逆操作会被记到外层的历史条目上
      throw new Error(
        `命令「${command.label}」在另一条命令执行期间被派发。` +
          `命令不得嵌套——需要组合多步时请用复合命令（composeCommands）。`,
      );
    }

    const before = this.#document;
    // invert 必须在 apply 之前算：逆操作往往要读"改之前"的状态
    const inverse = command.invert(before);

    this.#dispatching = true;
    let next: Document;
    try {
      const draft = DocumentDraft.from(before);
      command.apply(draft);
      next = draft.commit(this.#now());
    } catch (cause) {
      // 文档从未被替换，无需回滚
      throw new CommandFailedError(command, cause);
    } finally {
      this.#dispatching = false;
    }

    const changed = next !== before;
    this.#document = next;

    const result: CommandResult = { document: next, command, inverse, changed };
    for (const listener of this.#listeners) listener(result);
    return result;
  }

  /** 直接替换文档。**仅供历史系统撤销/重做使用**，不走监听器的常规路径。 */
  replaceDocument(document: Document): void {
    this.#document = document;
  }
}

/* ------------------------------------------------------------------ */
/* 复合命令                                                             */
/* ------------------------------------------------------------------ */

/**
 * 把多条命令合成一条。
 *
 * 逆操作是**各子命令逆操作的倒序**——顺序错了会得到一个看似成功却把文档
 * 弄乱的撤销，比直接失败更糟。
 */
export function composeCommands(label: string, commands: readonly Command[]): Command {
  return {
    kind: 'composite',
    label,
    cost: commands.reduce((sum, c) => sum + c.cost, 0),
    apply(draft) {
      for (const command of commands) command.apply(draft);
    },
    invert(document) {
      // 逐条算逆操作时要模拟中间状态：第二条命令的逆操作依赖第一条执行后的文档
      const inverses: Command[] = [];
      let current = document;
      for (const command of commands) {
        const inverse = command.invert(current);
        if (inverse === null) return null; // 有一条不可撤销，整体就不可撤销
        inverses.push(inverse);

        const draft = DocumentDraft.from(current);
        command.apply(draft);
        current = draft.commit(current.meta.modifiedAt);
      }
      inverses.reverse();
      return composeCommands(`撤销${label}`, inverses);
    },
  };
}
